import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { IconStar } from '../components/icons';
import { Chips, Empty, ErrorNote, Loading, PageHead, Pager, Pill, Stat } from '../components/ui';
import { api } from '../lib/api';
import { dateTime, rupees, VEHICLE_LABEL } from '../lib/format';
import { qk } from '../lib/realtime-cache';
import type { RatingPage, RatingRow } from '../lib/types';
import { ratingsRoute } from '../router';

export const TAG_LABEL: Record<string, string> = {
  SAFE_DRIVING: 'Safe driving',
  POLITE: 'Polite',
  CLEAN_VEHICLE: 'Clean vehicle',
  ON_TIME: 'On time',
  KNEW_ROUTE: 'Knew the route',
  RESPECTFUL: 'Respectful',
  RASH_DRIVING: 'Rash driving',
  RUDE: 'Rude',
  LATE: 'Late',
  DIRTY_VEHICLE: 'Dirty vehicle',
  ASKED_EXTRA_MONEY: 'Asked for extra money',
  WRONG_PICKUP: 'Wrong pickup point',
  NO_SHOW: 'Didn’t show up',
};
const BAD_TAGS = new Set(['RASH_DRIVING', 'RUDE', 'LATE', 'DIRTY_VEHICLE', 'ASKED_EXTRA_MONEY', 'WRONG_PICKUP', 'NO_SHOW']);

type Show = 'complaints' | 'all';
type By = 'any' | 'RIDER' | 'DRIVER';

export function Stars({ n }: { n: number }) {
  return (
    <span className="stars" aria-label={`${n} of 5 stars`}>
      {'★'.repeat(n)}
      <span className="off">{'★'.repeat(5 - n)}</span>
    </span>
  );
}

/** What riders say about drivers and drivers about riders. Complaints: 1–2 stars or a complaint tag. */
export function RatingsPage() {
  const search = ratingsRoute.useSearch();
  const nav = ratingsRoute.useNavigate();
  const show: Show = search.show ?? 'complaints';
  const by: By = search.by ?? 'any';
  const page = search.page ?? 1;

  const q = useQuery({
    queryKey: [...qk.ratings, search],
    queryFn: () =>
      api.get<RatingPage>('/admin/ratings', {
        complaints: show === 'complaints' ? true : undefined,
        byType: by === 'any' ? undefined : by,
        page,
        pageSize: 25,
      }),
    placeholderData: keepPreviousData,
  });
  const s = q.data?.summary;

  return (
    <>
      <PageHead title="Ratings" sub="Stars and complaints after each trip — riders about drivers, drivers about riders." />
      <div className="stats stats-3">
        <Stat
          icon={IconStar}
          label="Drivers, average"
          value={s?.ofDrivers.average != null ? `★ ${s.ofDrivers.average.toFixed(1)}` : '—'}
          foot={s ? `${s.ofDrivers.count} ratings from riders` : undefined}
        />
        <Stat
          icon={IconStar}
          label="Riders, average"
          value={s?.ofRiders.average != null ? `★ ${s.ofRiders.average.toFixed(1)}` : '—'}
          foot={s ? `${s.ofRiders.count} ratings from drivers` : undefined}
        />
        <Stat label={`${show === 'complaints' ? 'Complaints' : 'Ratings'} shown`} value={q.data ? q.data.total : '—'} foot={show === 'complaints' ? '1–2 stars or a complaint tag' : 'Every rating'} />
      </div>

      <div className="toolbar" style={{ marginTop: 18 }}>
        <Chips<Show>
          label="Show"
          value={show}
          onChange={(v) => void nav({ search: { ...search, show: v === 'complaints' ? undefined : v, page: undefined } })}
          options={[
            { value: 'complaints', label: 'Complaints' },
            { value: 'all', label: 'All ratings' },
          ]}
        />
        <Chips<By>
          label="From"
          value={by}
          onChange={(v) => void nav({ search: { ...search, by: v === 'any' ? undefined : v, page: undefined } })}
          options={[
            { value: 'any', label: 'Both' },
            { value: 'RIDER', label: 'About drivers' },
            { value: 'DRIVER', label: 'About riders' },
          ]}
        />
      </div>

      <section className="card">
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : !q.data?.items.length ? (
          <Empty title={show === 'complaints' ? 'No complaints' : 'No ratings yet'} icon={IconStar}>
            {show === 'complaints' ? 'Nothing at 1–2 stars or with a complaint tag.' : 'Ratings appear after completed trips.'}
          </Empty>
        ) : (
          <>
            <div className="rating-list" data-testid="ratings">
              {q.data.items.map((r) => (
                <RatingItem key={r.id} r={r} />
              ))}
            </div>
            <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
    </>
  );
}

function RatingItem({ r }: { r: RatingRow }) {
  const aboutDriver = r.byType === 'RIDER';
  const from = aboutDriver ? r.rider : r.driver;
  const about = aboutDriver ? r.driver : r.rider;
  return (
    <article className="rating-item" data-testid={`rating-${r.id}`}>
      <div className="stack-sm" style={{ gap: 4, minWidth: 0 }}>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <Stars n={r.stars} />
          {r.complaint && <Pill tone="bad">Complaint</Pill>}
          <span className="small faint">{aboutDriver ? 'Rider about driver' : 'Driver about rider'}</span>
        </div>
        <div>
          <strong>{from?.name ?? 'Someone'}</strong> <span className="faint">about</span>{' '}
          {about ? (
            aboutDriver ? (
              <Link to="/drivers/$driverId" params={{ driverId: about.id }}>
                {about.name ?? 'the driver'}
              </Link>
            ) : (
              <Link to="/riders/$riderId" params={{ riderId: about.id }}>
                {about.name ?? 'the rider'}
              </Link>
            )
          ) : (
            '—'
          )}
        </div>
        {r.comment && <p className="comment">“{r.comment}”</p>}
        {r.tags.length > 0 && (
          <div className="tags">
            {r.tags.map((t) => (
              <Pill key={t} tone={BAD_TAGS.has(t) ? 'bad' : 'good'}>
                {TAG_LABEL[t] ?? t}
              </Pill>
            ))}
          </div>
        )}
      </div>
      <div className="small" style={{ textAlign: 'right', minWidth: 0 }}>
        <Link to="/rides/$rideId" params={{ rideId: r.ride.id }}>
          {VEHICLE_LABEL[r.ride.vehicleType]} · {rupees(r.ride.farePaise)}
        </Link>
        <div className="faint">{dateTime(r.createdAt)}</div>
      </div>
    </article>
  );
}
